import { computed, ref, watch } from 'vue';

export type Appearance = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'simplex.appearance';
const media = window.matchMedia('(prefers-color-scheme: dark)');

// Storage can be blocked by browser policy (SecurityError); appearance then falls back to the system setting.
function readStored(): Appearance {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return value === 'light' || value === 'dark' ? value : 'system';
    } catch {
        return 'system';
    }
}

function writeStored(value: Appearance): void {
    try {
        localStorage.setItem(STORAGE_KEY, value);
    } catch {
        // The choice still applies for this session.
    }
}

const appearance = ref<Appearance>(readStored());
const systemDark = ref(media.matches);
media.addEventListener('change', (e) => { systemDark.value = e.matches; });

const isDark = computed(() => appearance.value === 'dark' || (appearance.value === 'system' && systemDark.value));

function apply(): void {
    document.documentElement.setAttribute('data-theme', isDark.value ? 'dark' : 'light');
}

watch(isDark, apply);
watch(appearance, writeStored);

/** Call in main.ts before mount so the page doesn't flash. */
export function createAppearanceController(): void {
    apply();
}

export function useAppearance() {
    return { appearance, isDark };
}

export const appearanceOptions: Record<Appearance, { label: string; icon: string; next: Appearance }> = {
    light: { label: 'Licht', icon: 'fas fa-sun', next: 'dark' },
    dark: { label: 'Donker', icon: 'fas fa-moon', next: 'system' },
    system: { label: 'Systeem', icon: 'fas fa-adjust', next: 'light' },
};
